import { useCallback, useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  Button,
  Select,
  FormControl,
  MenuItem,
  Menu,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import { parse } from 'csv-parse/sync';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';

import { reportAppError } from '@/shared/monitoring';
import {
  Unit,
  Lease,
  LeaseLifecycle,
  DoneSubState,
  LeaseSubState,
  ActionItem,
} from '@urbanmix-tech/shared-js';
import { Concession } from '@/types/lease';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { useLoading } from '@/shared/context/UIContextProvider';

import { FileUploadButton } from '@/components/shared/FileUploads/FileUploadButton';
import { ConfirmButtons } from '@/components/shared/ConfirmButtons/ConfirmButtons';
import {
  getUnitsByProjectId,
  saveUnits,
  saveLease,
  getLeases,
  getProjectById,
  saveActionItem,
} from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import {
  generateLeaseImportPreview,
  LeaseCreationPreview,
  toDate,
  CalculatedConcession,
} from './leaseImportHelpers';
import { LeaseCreationPreviewDialog } from './LeaseCreationPreviewDialog';

// Helper to create Concession entries from calculated concession
// The concession amount is stored as a multiplier of the monthly rent (number of months)
// For example: amount=1 means 1 month of free rent, amount=0.5 means half month, etc.
// NOTE: This function is kept for potential future use but is NOT currently used
// in the import phase. The calculation based on rent difference was found to be
// inaccurate for determining concessions during import.
// The formula used elsewhere: concession_dollar_value = amount * monthlyRent
function createConcessionEntries(
  calculatedConcession: CalculatedConcession | undefined,
  leaseStartDate: string,
  leaseEndDate: string
): { concessions: Concession[]; totalConcessionAmount: number } {
  if (!calculatedConcession) {
    return { concessions: [], totalConcessionAmount: 0 };
  }

  const { monthlyDifference, totalConcessionAmount, monthlyRent, effectiveRent, monthsOfConcession } = calculatedConcession;

  // Calculate the monthly concession as a multiplier of the monthly rent
  // monthlyDifference is the dollar amount of concession per month
  // We need to express this as a fraction of monthlyRent
  // amount * monthlyRent = monthlyDifference, so amount = monthlyDifference / monthlyRent
  const monthlyMultiplier = monthlyRent > 0 ? monthlyDifference / monthlyRent : 0;

  // Create concession entries for each month of the lease
  // Each month gets the same multiplier
  const startDate = dayjs(leaseStartDate);
  const endDate = dayjs(leaseEndDate);
  const concessions: Concession[] = [];

  let currentDate = startDate;
  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
    concessions.push({
      month: currentDate.month() + 1, // dayjs months are 0-indexed
      year: currentDate.year(),
      type: 'rentMultiplier',
      // amount is the multiplier: amount * monthlyRent = dollar value of concession for this month
      amount: Math.round(monthlyMultiplier * 10000) / 10000, // Round to 4 decimal places
      description: `Imported concession: $${monthlyRent.toLocaleString()} monthly rent reduced to $${effectiveRent.toLocaleString()} effective rent (${monthsOfConcession.toFixed(1)} months total)`,
    });
    currentDate = currentDate.add(1, 'month');
  }

  return {
    concessions,
    totalConcessionAmount,
  };
}

// Helper function to check if a lease has concession info
function hasConcessionInfo(lease: Partial<Lease> | null | undefined): boolean {
  if (!lease) return false;
  // Check if concessions array exists and has entries, or if totalConcessionAmount is set
  const hasConcessions = Array.isArray(lease.concessions) && lease.concessions.length > 0;
  const hasTotalAmount = typeof lease.totalConcessionAmount === 'number' && lease.totalConcessionAmount > 0;
  return hasConcessions || hasTotalAmount;
}

// Helper function to generate markdown description for concession verification task
function generateConcessionVerificationMarkdown(preview: LeaseCreationPreview): string {
  const { importData, existingLease } = preview;
  const lines: string[] = [];

  lines.push('**Action Required:** Verify concession information for this lease.');
  lines.push('');
  lines.push('No concession data was found in either the imported lease or the existing lease record.');
  lines.push('');
  lines.push('**Lease Details:**');
  lines.push('');

  const startDate = toDate(importData.leaseStartDate);
  const endDate = toDate(importData.leaseEndDate);
  lines.push(`- Unit: ${preview.unitId}`);
  lines.push(`- Start Date: ${startDate ? dayjs(startDate).format('MM/DD/YYYY') : 'N/A'}`);
  lines.push(`- End Date: ${endDate ? dayjs(endDate).format('MM/DD/YYYY') : 'N/A'}`);
  lines.push(`- Monthly Rent: $${(importData.monthlyRent || 0).toLocaleString()}`);

  if (existingLease) {
    lines.push('');
    lines.push('**Note:** An existing lease was found but also had no concession information.');
  }

  return lines.join('\n');
}

// Helper function to generate markdown description for lease conflict
function generateConflictMarkdown(preview: LeaseCreationPreview): string {
  const { details, importData, conflictType, selectedResolution } = preview;
  const lines: string[] = [];

  lines.push(`**Conflict Type:** ${conflictType || 'unknown'}`);
  lines.push('');
  lines.push(`**Resolution Applied:** ${selectedResolution || 'unknown'}`);
  lines.push('');

  // Vacant conflict — show existing lease info and end date confirmation prompt
  if (conflictType === 'vacant_conflict' && preview.existingLease) {
    const existingStart = toDate(preview.existingLease.leaseStartDate);
    const existingEnd = toDate(preview.existingLease.leaseEndDate);
    lines.push('**Existing Lease:**');
    lines.push('');
    lines.push(`- Start Date: ${existingStart ? dayjs(existingStart).format('MM/DD/YYYY') : 'N/A'}`);
    lines.push(`- End Date: ${existingEnd ? dayjs(existingEnd).format('MM/DD/YYYY') : 'N/A'}`);
    lines.push(`- Monthly Rent: $${(preview.existingLease.monthlyRent || 0).toLocaleString()}`);
    lines.push('');
    if (selectedResolution === 'end_existing') {
      lines.push(`**Lease end date set to:** ${dayjs().format('MM/DD/YYYY')} (import date)`);
      lines.push('');
      lines.push('Please verify the actual move-out date and update the lease end date accordingly.');
    } else if (selectedResolution === 'cancel_existing') {
      lines.push('Lease has been canceled (was never executed).');
    }
    return lines.join('\n');
  }

  if (details.startDateChanged || details.endDateChanged || details.rentChanged || details.depositChanged) {
    lines.push('**Changes Detected:**');
    lines.push('');

    if (details.startDateChanged) {
      const fromDate = details.startDateChanged.from ? dayjs(details.startDateChanged.from).format('MM/DD/YYYY') : 'N/A';
      const toDateVal = details.startDateChanged.to ? dayjs(details.startDateChanged.to).format('MM/DD/YYYY') : 'N/A';
      lines.push(`- Start Date: ${fromDate} → ${toDateVal}`);
    }

    if (details.endDateChanged) {
      const fromDate = details.endDateChanged.from ? dayjs(details.endDateChanged.from).format('MM/DD/YYYY') : 'N/A';
      const toDateVal = details.endDateChanged.to ? dayjs(details.endDateChanged.to).format('MM/DD/YYYY') : 'N/A';
      lines.push(`- End Date: ${fromDate} → ${toDateVal}`);
    }

    if (details.rentChanged) {
      lines.push(`- Monthly Rent: $${details.rentChanged.from.toLocaleString()} → $${details.rentChanged.to.toLocaleString()}`);
    }

    if (details.depositChanged) {
      lines.push(`- Security Deposit: $${details.depositChanged.from.toLocaleString()} → $${details.depositChanged.to.toLocaleString()}`);
    }
  } else {
    // For new leases (past_lease type with no existing lease)
    lines.push('**Import Data:**');
    lines.push('');
    const startDate = toDate(importData.leaseStartDate);
    const endDate = toDate(importData.leaseEndDate);
    lines.push(`- Start Date: ${startDate ? dayjs(startDate).format('MM/DD/YYYY') : 'N/A'}`);
    lines.push(`- End Date: ${endDate ? dayjs(endDate).format('MM/DD/YYYY') : 'N/A'}`);
    lines.push(`- Monthly Rent: $${(importData.monthlyRent || 0).toLocaleString()}`);
    if (importData.securityDeposit) {
      lines.push(`- Security Deposit: $${importData.securityDeposit.toLocaleString()}`);
    }
  }

  return lines.join('\n');
}

// Helper function to generate markdown for skip_and_check action items
function generateSkipAndCheckMarkdown(preview: LeaseCreationPreview): string {
  const { importData, existingLease } = preview;
  const lines: string[] = [];

  lines.push('**Action Required:** Verify that the current lease for this unit is up-to-date.');
  lines.push('');
  lines.push('During rent roll import, the imported data appeared outdated or conflicting.');
  lines.push('The import was skipped, but please verify the existing lease is correct.');
  lines.push('');

  const importStart = toDate(importData.leaseStartDate);
  const importEnd = toDate(importData.leaseEndDate);
  lines.push('**Imported Data (skipped):**');
  lines.push('');
  lines.push(`- Monthly Rent: $${(importData.monthlyRent || 0).toLocaleString()}`);
  lines.push(`- Start Date: ${importStart ? dayjs(importStart).format('MM/DD/YYYY') : 'N/A'}`);
  lines.push(`- End Date: ${importEnd ? dayjs(importEnd).format('MM/DD/YYYY') : 'N/A'}`);

  if (existingLease) {
    const existingStart = toDate(existingLease.leaseStartDate);
    const existingEnd = toDate(existingLease.leaseEndDate);
    lines.push('');
    lines.push('**Existing Lease:**');
    lines.push('');
    lines.push(`- Monthly Rent: $${(existingLease.monthlyRent || 0).toLocaleString()}`);
    lines.push(`- Start Date: ${existingStart ? dayjs(existingStart).format('MM/DD/YYYY') : 'N/A'}`);
    lines.push(`- End Date: ${existingEnd ? dayjs(existingEnd).format('MM/DD/YYYY') : 'N/A'}`);
  }

  return lines.join('\n');
}

// Helper function to extract floor and type from unit ID
// Examples: 4A -> floor: '4', type: 'A'
//           3AE -> floor: '3', type: 'AE'
const extractFloorAndType = (unitId: string): { floor: string; type: string } | null => {
  if (!unitId) return null;

  // Match: one or more digits at the start, followed by 1-2 letters
  const match = unitId.match(/^(\d+)([A-Z]{1,2})$/i);

  if (match) {
    return {
      floor: match[1],
      type: match[2].toUpperCase(),
    };
  }
  return null;
};

export type Parameters = {
  units: Unit[];
};

type BuildingModelEditProps = {
  projectId: string;
};

type InputFormats = 'appfolio' | 'yardi-xls' | 'yardi-csv' | 'entrata-xls' | 'custom';

type HeaderMap = {
  key: string;
  map: (value: string) => unknown;
};

type HeaderMapping = {
  [key: string]: {
    [key: string]: HeaderMap[];
  };
};

const headerMapping: HeaderMapping = {
  appfolio: {
    Unit: [{ key: 'id', map: (value: unknown) => value }],
    'Lease From': [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    Rent: [
      {
        key: 'actualRent',
        map: (value: string) => parseFloat(value.replace(/,/g, '')),
      },
    ],
    Deposit: [
      {
        key: 'residentDeposit',
        map: (value: string) => parseFloat(value.replace(/,/g, '')),
      },
    ],
    Status: [
      {
        key: 'currentState',
        map: (value: string) => {
          switch (value) {
            case 'Current':
              return LeaseLifecycle.DONE;
            default:
              return LeaseLifecycle.LEASE;
          }
        },
      },
      {
        key: 'currentSubState',
        map: (value: string) => {
          switch (value) {
            case 'Current':
              return DoneSubState.Occupied;
            default:
              return LeaseSubState.Publish;
          }
        },
      },
    ],
  },
  'yardi-xls': {
    Name: [{ key: 'tenantName', map: (value: string) => String(value).trim() }],
    Unit: [{ key: 'id', map: (value: string) => String(value).trim() }],
    Type: [{ key: 'type', map: (value: string) => String(value).trim() }],
    'Sq Ft': [{ key: 'sqft', map: (value: string) => parseFloat(String(value)) || 0 }],
    'Move In Date': [
      {
        key: 'actualMoveInDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    LeaseFrom: [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    'Market Rent': [
      {
        key: 'marketRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Tenant Rent': [
      {
        key: 'actualRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Sec Deposit': [
      {
        key: 'residentDeposit',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    // All Yardi imports are occupied units (they have active leases)
    _occupied: [
      { key: 'currentState', map: () => LeaseLifecycle.DONE },
      { key: 'currentSubState', map: () => DoneSubState.Occupied },
    ],
  },
  'yardi-csv': {
    Name: [{ key: 'tenantName', map: (value: string) => String(value).trim() }],
    Unit: [{ key: 'id', map: (value: string) => String(value).trim() }],
    Type: [{ key: 'type', map: (value: string) => String(value).trim() }],
    'Sq Ft': [{ key: 'sqft', map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0 }],
    'Move In Date': [
      {
        key: 'actualMoveInDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    LeaseFrom: [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').toDate() : null;
        },
      },
    ],
    'Market Rent': [
      {
        key: 'marketRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Tenant Rent': [
      {
        key: 'actualRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Sec Deposit': [
      {
        key: 'residentDeposit',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    // All Yardi imports are occupied units (they have active leases)
    _occupied: [
      { key: 'currentState', map: () => LeaseLifecycle.DONE },
      { key: 'currentSubState', map: () => DoneSubState.Occupied },
    ],
  },
  'entrata-xls': {
    Unit: [{ key: 'id', map: (value: string) => String(value).trim() }],
    'Size (Sqft)': [{ key: 'sqft', map: (value: string) => parseFloat(String(value)) || 0 }],
    'Market Rent': [
      {
        key: 'marketRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    Deposit: [
      {
        key: 'residentDeposit',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    Bedrooms: [{ key: 'bedrooms', map: (value: string) => parseFloat(String(value)) || 0 }],
    Bathrooms: [{ key: 'bathrooms', map: (value: string) => parseFloat(String(value)) || 0 }],
    Status: [
      {
        key: 'currentState',
        map: (value: string) => {
          if (String(value).toLowerCase().startsWith('occupied')) {
            return LeaseLifecycle.DONE;
          }
          return LeaseLifecycle.LEASE;
        },
      },
      {
        key: 'currentSubState',
        map: (value: string) => {
          if (String(value).toLowerCase().startsWith('occupied')) {
            return DoneSubState.Occupied;
          }
          return LeaseSubState.Publish;
        },
      },
    ],
  },
  custom: {},
};

// Lease header mapping for creating Lease entities from CSV data
const leaseHeaderMapping: HeaderMapping = {
  appfolio: {
    Unit: [{ key: 'unitId', map: (value: unknown) => value }],
    'Lease From': [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    Rent: [
      {
        key: 'monthlyRent',
        map: (value: string) => parseFloat(value.replace(/,/g, '')),
      },
    ],
    'Effective Rent': [
      {
        key: 'effectiveRent',
        map: (value: string) => parseFloat(value.replace(/,/g, '')),
      },
    ],
    Deposit: [
      {
        key: 'securityDeposit',
        map: (value: string) => parseFloat(value.replace(/,/g, '')),
      },
    ],
  },
  'yardi-xls': {
    Unit: [{ key: 'unitId', map: (value: string) => String(value).trim() }],
    Name: [{ key: 'eLeaseFields.tenantName', map: (value: string) => String(value).trim() }],
    'Lease Type': [{ key: 'eLeaseFields.leaseType', map: (value: string) => String(value).trim() }],
    'Move In Date': [
      {
        key: 'eLeaseFields.moveInDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    LeaseFrom: [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Tenant Rent': [
      {
        key: 'monthlyRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Sec Deposit': [
      {
        key: 'securityDeposit',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
  },
  'yardi-csv': {
    Unit: [{ key: 'unitId', map: (value: string) => String(value).trim() }],
    Name: [{ key: 'eLeaseFields.tenantName', map: (value: string) => String(value).trim() }],
    'Lease Type': [{ key: 'eLeaseFields.leaseType', map: (value: string) => String(value).trim() }],
    'Move In Date': [
      {
        key: 'eLeaseFields.moveInDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    LeaseFrom: [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Market Rent': [
      {
        key: 'marketRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Tenant Rent': [
      {
        key: 'monthlyRent',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Sec Deposit': [
      {
        key: 'securityDeposit',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    // Concession columns - CSV format includes these two additional columns
    'Concession (months)': [
      {
        key: 'concessionMonths',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
    'Concession (Amount)': [
      {
        key: 'totalConcessionAmount',
        map: (value: string) => parseFloat(String(value).replace(/,/g, '')) || 0,
      },
    ],
  },
  'entrata-xls': {
    Unit: [{ key: 'unitId', map: (value: string) => String(value).trim() }],
    Tenant: [{ key: 'eLeaseFields.tenantName', map: (value: string) => String(value).trim() }],
    'Lease From': [
      {
        key: 'leaseStartDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    'Lease To': [
      {
        key: 'leaseEndDate',
        map: (value: string) => {
          return value ? dayjs(value, 'MM/DD/YYYY').format('YYYY-MM-DD') : null;
        },
      },
    ],
    Status: [
      {
        key: 'status',
        map: (value: string) => String(value).trim(),
      },
    ],
    Email: [{ key: 'eLeaseFields.tenantEmail', map: (value: string) => String(value).trim() }],
    Phone: [{ key: 'eLeaseFields.tenantPhone', map: (value: string) => String(value).trim() }],
  },
  custom: {},
};

// Parse Entrata Excel file format
// Auto-detects header row by scanning for a row containing "Unit" column
const parseEntrataExcel = async (file: File): Promise<{ headers: string[], records: string[][] }> => {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.worksheets[0];

  // Find the header row by looking for a row that contains "Unit"
  let headerRowNum = 0;
  for (let rowNum = 1; rowNum <= Math.min(10, worksheet.rowCount); rowNum++) {
    const row = worksheet.getRow(rowNum);
    let found = false;
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (String(cell.value).trim() === 'Unit') found = true;
    });
    if (found) {
      headerRowNum = rowNum;
      break;
    }
  }

  if (headerRowNum === 0) {
    throw new Error('Could not find header row with "Unit" column in Entrata file');
  }

  const headerRow = worksheet.getRow(headerRowNum);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const value = cell.value;
    headers[colNumber - 1] = value ? String(value).trim() : '';
  });

  // Data starts at the row after the header
  const records: string[][] = [];
  for (let rowNum = headerRowNum + 1; rowNum <= worksheet.rowCount; rowNum++) {
    const row = worksheet.getRow(rowNum);
    const record: string[] = [];
    let hasData = false;

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      let cellValue = '';
      const value = cell.value;

      if (value !== null && value !== undefined) {
        if (value instanceof Date) {
          cellValue = dayjs(value).format('MM/DD/YYYY');
        } else if (typeof value === 'object' && 'result' in value) {
          cellValue = String(value.result || '');
        } else {
          cellValue = String(value);
        }
        if (cellValue.trim()) hasData = true;
      }
      record[colNumber - 1] = cellValue;
    });

    // Skip empty rows and summary/total rows
    const firstValue = record[0] || '';
    const unitValue = record[1] || '';
    if (hasData && !firstValue.toLowerCase().includes('grand total') && unitValue.trim()) {
      records.push(record);
    }
  }

  return { headers, records };
};

// Parse Yardi Excel file format
// Structure: rows 0-6 are header metadata, row 7 is column headers, row 8+ is data
const parseYardiExcel = async (file: File): Promise<{ headers: string[], records: string[][] }> => {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.worksheets[0]; // Sheet1

  // Row 8 is headers (1-indexed in ExcelJS)
  const headerRow = worksheet.getRow(8);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const value = cell.value;
    headers[colNumber - 1] = value ? String(value).trim() : '';
  });

  // Data starts at row 9
  const records: string[][] = [];
  for (let rowNum = 9; rowNum <= worksheet.rowCount; rowNum++) {
    const row = worksheet.getRow(rowNum);
    const record: string[] = [];
    let hasData = false;

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      let cellValue = '';
      const value = cell.value;

      if (value !== null && value !== undefined) {
        // Handle Date objects from Excel
        if (value instanceof Date) {
          cellValue = dayjs(value).format('MM/DD/YYYY');
        } else if (typeof value === 'object' && 'result' in value) {
          // Handle formula results
          cellValue = String(value.result || '');
        } else {
          cellValue = String(value);
        }
        if (cellValue.trim()) hasData = true;
      }
      record[colNumber - 1] = cellValue;
    });

    // Skip empty rows and summary rows (containing "Total")
    const unitValue = record[1] || '';
    if (hasData && unitValue.trim() && !unitValue.toLowerCase().includes('total')) {
      records.push(record);
    }
  }

  return { headers, records };
};

export function ImportRentRoll({ projectId }: BuildingModelEditProps) {
  const { showAlert } = useAlert();
  const { createLoading } = useLoading();
  const { userData } = useAuth();

  const [inputFormat, setInputFormat] = useState<InputFormats>('appfolio');
  const [unitUpdates, setUnitUpdates] = useState<Partial<Unit>[]>([]);
  const [leaseUpdates, setLeaseUpdates] = useState<Partial<Lease>[]>([]);
  const [existingLeases, setExistingLeases] = useState<Lease[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
  const [project, setProject] = useState<any>(null);
  const [entrataUnitFile, setEntrataUnitFile] = useState<string | null>(null);
  const [entrataTenantFile, setEntrataTenantFile] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{
    toCreate: LeaseCreationPreview[];
    toSkip: LeaseCreationPreview[];
    summary: {
      totalImported: number;
      newLeases: number;
      updatedLeases: number;
      skipped: number;
    };
  } | null>(null);

  // Fetch existing leases and project data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const projectData = await getProjectById(projectId);
        setProject(projectData);

        // Subscribe to leases
        const unsubscribe = await getLeases(
          { projectId },
          (leases: Lease[]) => {
            setExistingLeases(leases.filter((l) => l.status === 'valid'));
          }
        );

        return unsubscribe as () => void;
      } catch (err) {
        reportAppError(err as Error);
      }
    };

    if (projectId) {
      const cleanupPromise = loadData();
      return () => {
        cleanupPromise?.then((cleanup) => cleanup?.());
      };
    }
  }, [projectId]);

  // Helper to set a value using dot notation (e.g., 'eLeaseFields.tenantName')
  const setNestedValue = (obj: Record<string, unknown>, key: string, value: unknown) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      if (!obj[parent]) {
        obj[parent] = {};
      }
      (obj[parent] as Record<string, unknown>)[child] = value;
    } else {
      obj[key] = value;
    }
  };

  // Process records using the mapping configuration
  const processRecords = (
    headers: string[],
    records: string[][],
    mapping: HeaderMapping[string],
    isLeaseMapping: boolean = false
  ) => {
    return records.map((record) => {
      const newRecord: Record<string, unknown> = {};

      // Initialize eLeaseFields for lease records
      if (isLeaseMapping) {
        newRecord.eLeaseFields = {};
      }

      record.forEach((item, index) => {
        const headerKey = headers[index];
        const mappings: HeaderMap[] = mapping[headerKey];
        if (mappings) {
          mappings.forEach((m) => {
            const value = m.map(item);
            setNestedValue(newRecord, m.key, value);
          });
        }
      });

      // Apply occupied/vacant state for Yardi format based on tenant name
      if (!isLeaseMapping && mapping['_occupied']) {
        const isVacant = (newRecord.tenantName as string)?.toLowerCase() === 'vacant';
        if (!isVacant) {
          mapping['_occupied'].forEach((m) => {
            newRecord[m.key] = m.map('');
          });
        } else {
          newRecord.currentState = LeaseLifecycle.LEASE;
          newRecord.currentSubState = LeaseSubState.Publish;
        }
      }

      return newRecord;
    });
  };

  const handleSelectFile = useCallback(
    async (file: File) => {
      const loader = createLoading();
      const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

      loader.startLoading({
        title: isExcel ? 'Reading Excel File...' : 'Reading CSV File...',
      });

      try {
        let headers: string[];
        let records: string[][];

        if (isExcel && inputFormat === 'yardi-xls') {
          // Parse Excel file for Yardi format
          const excelData = await parseYardiExcel(file);
          headers = excelData.headers;
          records = excelData.records;
        } else {
          // Parse CSV file
          const reader = new FileReader();
          const result = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
          });

          let processedResult = result;
          if (inputFormat === 'appfolio' || inputFormat === 'yardi-csv') {
            // get rid of blank lines
            const lines = processedResult
              .split('\n')
              .filter((line) => line.trim() !== '');

            // get rid of the last line if it starts with "Total"
            if (lines[lines.length - 1]?.startsWith('Total')) {
              lines.pop();
            }
            processedResult = lines.join('\n');
          }

          const rawRecords = parse(processedResult) as string[][];
          headers = rawRecords[0];
          records = rawRecords.slice(1);
        }

        // Process unit updates
        const newUpdates = processRecords(headers, records, headerMapping[inputFormat], false);

        // Process lease updates with nested field support
        const newLeaseUpdates = processRecords(headers, records, leaseHeaderMapping[inputFormat], true);

        setUnitUpdates(newUpdates);
        setLeaseUpdates(newLeaseUpdates as Partial<Lease>[]);

        loader.stopLoading();
      } catch (err) {
        loader.stopLoading();
        reportAppError(err as Error);
        showAlert('Failed to parse file', 'error');
      }
    },
    [createLoading, inputFormat, showAlert]
  );

  // Handle Entrata Unit Directory file upload
  const handleEntrataUnitFile = useCallback(
    async (file: File) => {
      const loader = createLoading();
      loader.startLoading({ title: 'Reading Unit Directory...' });

      try {
        const excelData = await parseEntrataExcel(file);
        const newUpdates = processRecords(excelData.headers, excelData.records, headerMapping['entrata-xls'], false);
        setUnitUpdates(newUpdates);
        setEntrataUnitFile(file.name);
        loader.stopLoading();
      } catch (err) {
        loader.stopLoading();
        reportAppError(err as Error);
        showAlert('Failed to parse Unit Directory file', 'error');
      }
    },
    [createLoading, showAlert]
  );

  // Handle Entrata Tenant Listing file upload
  const handleEntrataTenantFile = useCallback(
    async (file: File) => {
      const loader = createLoading();
      loader.startLoading({ title: 'Reading Tenant Listing...' });

      try {
        const excelData = await parseEntrataExcel(file);
        const newLeaseUpdates = processRecords(excelData.headers, excelData.records, leaseHeaderMapping['entrata-xls'], true);

        // Filter to only main tenants / lessees (skip roommates without lease end dates)
        const filteredLeases = (newLeaseUpdates as Partial<Lease>[]).filter(
          (lease) => lease.leaseStartDate && lease.leaseEndDate
        );

        setLeaseUpdates(filteredLeases);

        // Also update unitUpdates with lease dates and tenant state for units that have tenants
        setUnitUpdates((prev) => {
          const updated = prev.map((unit) => {
            const lease = filteredLeases.find((l) => l.unitId === unit.id);
            if (lease) {
              return {
                ...unit,
                leaseStartDate: lease.leaseStartDate ? dayjs(lease.leaseStartDate).toDate() : undefined,
                leaseEndDate: lease.leaseEndDate ? dayjs(lease.leaseEndDate).toDate() : undefined,
              };
            }
            return unit;
          });
          return updated;
        });

        setEntrataTenantFile(file.name);
        loader.stopLoading();
      } catch (err) {
        loader.stopLoading();
        reportAppError(err as Error);
        showAlert('Failed to parse Tenant Listing file', 'error');
      }
    },
    [createLoading, showAlert]
  );

  const handleShowPreview = () => {
    const preview = generateLeaseImportPreview(leaseUpdates, existingLeases);
    setPreviewData(preview);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
  };

  const handleConfirmImport = async (previews: LeaseCreationPreview[]) => {
    setPreviewDialogOpen(false);
    await handleUpdateUnits(previews);
  };

  const handleUpdateUnits = async (previews: LeaseCreationPreview[]) => {
    const loader = createLoading();

    loader.startLoading({
      title: 'Updating units and leases...',
    });

    try {
      const existingUnits = await getUnitsByProjectId(projectId);

      const newUnits = existingUnits.map((unit) => {
        const updates = unitUpdates.find((update) => update.id === unit.id);
        if (updates) {
          Object.assign(unit, updates);
        }
        return unit;
      });

      // Process leases based on user's conflict resolution choices
      const leasesToSave: Lease[] = [];
      const leasesToCancel: Lease[] = [];
      let skippedCount = 0;
      let createdCount = 0;
      let updatedCount = 0;
      let cancelledCount = 0;

      for (const preview of previews) {
        const { importData, existingLease, selectedResolution, action, conflictType } = preview;

        // Skip if user chose to skip, or if auto-skipped (matching data)
        if (action === 'skip' || selectedResolution === 'skip' || selectedResolution === 'skip_and_check') {
          skippedCount++;
          continue;
        }

        // Concession calculation from rent difference was removed as it was found to be inaccurate
        // Concessions should be explicitly provided in the import data, not calculated
        const leaseStartDateStr = (importData.leaseStartDate as string) || new Date().toISOString().split('T')[0];
        const leaseEndDateStr = (importData.leaseEndDate as string) || new Date().toISOString().split('T')[0];

        // Use imported concession data if available (from Yardi CSV format)
        const importedConcessionMonths = (importData as Record<string, unknown>).concessionMonths as number | undefined;
        const importedConcessionAmount = (importData.totalConcessionAmount as number) || 0;

        // Build concession entries if we have imported concession data
        let concessionData: {
          concessions: Concession[];
          totalConcessionAmount: number
        };

        if (importedConcessionAmount > 0 || (importedConcessionMonths && importedConcessionMonths > 0)) {
          // Create concession entries for each month of the lease
          const startDate = dayjs(leaseStartDateStr);
          const endDate = dayjs(leaseEndDateStr);
          const concessions: Concession[] = [];
          const monthlyRent = (importData.monthlyRent as number) || 0;

          // Calculate total concession amount: if only months provided, calculate from monthly rent
          const totalConcessionAmount = importedConcessionAmount > 0
            ? importedConcessionAmount
            : (importedConcessionMonths || 0) * monthlyRent;

          // Calculate lease length
          const leaseMonths = Math.max(1, endDate.diff(startDate, 'month', true));

          // Monthly concession multiplier (amount as fraction of monthly rent per month)
          const monthlyMultiplier = monthlyRent > 0 ? (totalConcessionAmount / leaseMonths) / monthlyRent : 0;

          let currentDate = startDate;
          while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'month')) {
            concessions.push({
              month: currentDate.month() + 1, // dayjs months are 0-indexed
              year: currentDate.year(),
              type: 'rentMultiplier',
              amount: Math.round(monthlyMultiplier * 10000) / 10000,
              description: `Imported concession: ${importedConcessionMonths || (totalConcessionAmount / monthlyRent).toFixed(1)} months ($${totalConcessionAmount.toLocaleString()} total)`,
            });
            currentDate = currentDate.add(1, 'month');
          }

          concessionData = { concessions, totalConcessionAmount };
        } else {
          concessionData = { concessions: [], totalConcessionAmount: 0 };
        }

        // Handle based on action and resolution
        if (action === 'create') {
          // Creating a new lease (no conflict or renewal)
          if (conflictType === 'renewal' && existingLease) {
            // Renewal: Create new lease linked to existing lease
            const newLeaseId = `lease_${importData.unitId}_${Date.now()}`;

            // Update existing lease with link to new lease
            const renewedLease: Lease = {
              ...existingLease,
              renewedToLeaseId: newLeaseId,
              updatedAt: new Date(),
            };
            leasesToSave.push(renewedLease);

            // Create new lease linked to existing lease
            const newLease: Lease = {
              id: newLeaseId,
              projectId,
              buildingId: project?.buildings?.[0]?.id || 1,
              unitId: importData.unitId as string,
              leaseStartDate: leaseStartDateStr,
              leaseEndDate: leaseEndDateStr,
              monthlyRent: (importData.monthlyRent as number) || 0,
              securityDeposit: importData.securityDeposit as number | undefined,
              previousLeaseId: existingLease.id,
              concessions: concessionData.concessions as any, // TODO: remove cast after shared-js update
              totalConcessionAmount: concessionData.totalConcessionAmount,
              status: 'valid',
              eLeaseFields: {},
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            leasesToSave.push(newLease);
            createdCount++;
          } else {
            // New lease (no existing lease, or separate lease period)
            const newLease: Lease = {
              id: `lease_${importData.unitId}_${Date.now()}`,
              projectId,
              buildingId: project?.buildings?.[0]?.id || 1,
              unitId: importData.unitId as string,
              leaseStartDate: leaseStartDateStr,
              leaseEndDate: leaseEndDateStr,
              monthlyRent: (importData.monthlyRent as number) || 0,
              securityDeposit: importData.securityDeposit as number | undefined,
              concessions: concessionData.concessions as any, // TODO: remove cast after shared-js update
              totalConcessionAmount: concessionData.totalConcessionAmount,
              status: 'valid',
              eLeaseFields: {},
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            leasesToSave.push(newLease);
            createdCount++;
          }
        } else if (action === 'update') {
          // Updating existing lease (only rent/deposit changed, or user chose to overwrite)
          if (!existingLease) continue; // Safety check

          if (preview.hasConflict && selectedResolution === 'cancel_existing_create_new') {
            // User chose to cancel existing and create new
            const cancelledLease: Lease = {
              ...existingLease,
              status: 'canceled',
              updatedAt: new Date(),
            };
            leasesToCancel.push(cancelledLease);
            cancelledCount++;

            // Create new lease
            const newLease: Lease = {
              id: `lease_${importData.unitId}_${Date.now()}`,
              projectId,
              buildingId: project?.buildings?.[0]?.id || 1,
              unitId: importData.unitId as string,
              leaseStartDate: leaseStartDateStr,
              leaseEndDate: leaseEndDateStr,
              monthlyRent: (importData.monthlyRent as number) || 0,
              securityDeposit: importData.securityDeposit as number | undefined,
              concessions: concessionData.concessions as any, // TODO: remove cast after shared-js update
              totalConcessionAmount: concessionData.totalConcessionAmount,
              status: 'valid',
              eLeaseFields: {},
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            leasesToSave.push(newLease);
            createdCount++;
          } else if (selectedResolution === 'end_existing') {
            // End existing lease — set end date to today, keep status valid
            const endedLease: Lease = {
              ...existingLease,
              leaseEndDate: new Date().toISOString().split('T')[0],
              updatedAt: new Date(),
            };
            leasesToSave.push(endedLease);
            updatedCount++;
          } else if (selectedResolution === 'cancel_existing') {
            // Cancel existing lease — was never executed
            const cancelledLease: Lease = {
              ...existingLease,
              status: 'canceled',
              updatedAt: new Date(),
            };
            leasesToCancel.push(cancelledLease);
            cancelledCount++;
          } else if (preview.hasConflict && selectedResolution === 'renewal') {
            // User manually chose renewal for a conflict case
            const newLeaseId = `lease_${importData.unitId}_${Date.now()}`;

            // Update existing lease with link to new lease
            const renewedLease: Lease = {
              ...existingLease,
              renewedToLeaseId: newLeaseId,
              updatedAt: new Date(),
            };
            leasesToSave.push(renewedLease);

            // Create new lease linked to existing lease
            const newLease: Lease = {
              id: newLeaseId,
              projectId,
              buildingId: project?.buildings?.[0]?.id || 1,
              unitId: importData.unitId as string,
              leaseStartDate: leaseStartDateStr,
              leaseEndDate: leaseEndDateStr,
              monthlyRent: (importData.monthlyRent as number) || 0,
              securityDeposit: importData.securityDeposit as number | undefined,
              previousLeaseId: existingLease.id,
              concessions: concessionData.concessions as any, // TODO: remove cast after shared-js update
              totalConcessionAmount: concessionData.totalConcessionAmount,
              status: 'valid',
              eLeaseFields: {},
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            leasesToSave.push(newLease);
            createdCount++;
          } else {
            // Update existing lease (default or overwrite)
            // For updates, also update concessions if they were calculated
            const updatedLease: Lease = {
              ...existingLease,
              leaseStartDate: leaseStartDateStr || existingLease.leaseStartDate,
              leaseEndDate: leaseEndDateStr || existingLease.leaseEndDate,
              monthlyRent: (importData.monthlyRent as number) ?? existingLease.monthlyRent,
              securityDeposit: (importData.securityDeposit as number | undefined) ?? existingLease.securityDeposit,
              // Update concessions if new ones were calculated, otherwise keep existing
              concessions: concessionData.concessions.length > 0 ? concessionData.concessions as any : existingLease.concessions, // TODO: remove cast after shared-js update
              totalConcessionAmount: concessionData.totalConcessionAmount > 0 ? concessionData.totalConcessionAmount : existingLease.totalConcessionAmount,
              updatedAt: new Date(),
            };
            leasesToSave.push(updatedLease);
            updatedCount++;
          }
        }
      }

      // Save units
      await saveUnits(projectId, newUnits);

      // Save cancelled leases first (so they're marked as cancelled)
      await Promise.all(leasesToCancel.map((lease) => saveLease(lease)));

      // Save all new/updated leases
      await Promise.all(leasesToSave.map((lease) => saveLease(lease)));

      // Create action items for conflicts where user didn't skip
      const conflictPreviews = previews.filter(
        (p) => (p.hasConflict || p.conflictType === 'past_lease') &&
               (p.selectedResolution !== 'skip' && p.selectedResolution !== 'skip_and_check')
      );

      if (conflictPreviews.length > 0 && userData) {
        const actionItemPromises = conflictPreviews.map((preview) => {
          const isEndExisting = preview.selectedResolution === 'end_existing';
          const actionItem: ActionItem = {
            id: isEndExisting
              ? `action_confirm_lease_end_${preview.unitId}_${Date.now()}`
              : `action_lease_conflict_${preview.unitId}_${Date.now()}`,
            projectId,
            buildingId: project?.buildings?.[0]?.id || 1,
            unitId: preview.unitId,
            title: isEndExisting ? 'Confirm lease end date' : 'Review lease data conflict',
            category: 'lease',
            status: 'active',
            description: generateConflictMarkdown(preview),
            owner: {
              userId: userData.id,
              email: userData.email,
            },
            severtity: 'regular',
            dueDate: new Date(),
            startDate: new Date(),
            reminderFrequency: 0,
            action_type: { type: 'reminder', inputType: 'text', reminder: false },
            createDate: new Date(),
            updateDate: new Date(),
          };
          return saveActionItem(actionItem);
        });

        await Promise.all(actionItemPromises);
        showAlert(
          `Created ${conflictPreviews.length} action item${conflictPreviews.length !== 1 ? 's' : ''} to review lease conflicts.`,
          'info'
        );
      }

      // Create action items for skip_and_check selections
      const skipAndCheckPreviews = previews.filter(
        (p) => p.selectedResolution === 'skip_and_check'
      );

      if (skipAndCheckPreviews.length > 0 && userData) {
        const skipCheckPromises = skipAndCheckPreviews.map((preview) => {
          const actionItem: ActionItem = {
            id: `action_verify_lease_${preview.unitId}_${Date.now()}`,
            projectId,
            buildingId: project?.buildings?.[0]?.id || 1,
            unitId: preview.unitId,
            title: 'Verify current lease data',
            category: 'lease',
            status: 'active',
            description: generateSkipAndCheckMarkdown(preview),
            owner: {
              userId: userData.id,
              email: userData.email,
            },
            severtity: 'regular',
            dueDate: new Date(),
            startDate: new Date(),
            reminderFrequency: 0,
            action_type: { type: 'reminder', inputType: 'text', reminder: false },
            createDate: new Date(),
            updateDate: new Date(),
          };
          return saveActionItem(actionItem);
        });

        await Promise.all(skipCheckPromises);
        showAlert(
          `Created ${skipAndCheckPreviews.length} action item${skipAndCheckPreviews.length !== 1 ? 's' : ''} to verify lease data.`,
          'info'
        );
      }

      // Create action items for leases missing concession info
      // Only for leases that were processed (not skipped) and where both
      // the imported lease and existing lease (if any) lack concession data
      // Skip affordable units since they typically don't have concessions
      const missingConcessionPreviews = previews.filter(
        (p) => {
          const unit = existingUnits.find((u) => u.id === p.unitId);
          const isAffordable = unit?.affordable === true;
          return p.selectedResolution !== 'skip' &&
                 p.action !== 'skip' &&
                 !isAffordable &&
                 !hasConcessionInfo(p.importData) &&
                 !hasConcessionInfo(p.existingLease);
        }
      );

      if (missingConcessionPreviews.length > 0 && userData) {
        const concessionActionItemPromises = missingConcessionPreviews.map((preview) => {
          const actionItem: ActionItem = {
            id: `action_verify_concession_${preview.unitId}_${Date.now()}`,
            projectId,
            buildingId: project?.buildings?.[0]?.id || 1,
            unitId: preview.unitId,
            title: 'Verify lease concession info',
            category: 'lease',
            status: 'active',
            description: generateConcessionVerificationMarkdown(preview),
            owner: {
              userId: userData.id,
              email: userData.email,
            },
            severtity: 'regular',
            dueDate: new Date(),
            startDate: new Date(),
            reminderFrequency: 0,
            action_type: { type: 'reminder', inputType: 'text', reminder: false },
            createDate: new Date(),
            updateDate: new Date(),
          };
          return saveActionItem(actionItem);
        });

        await Promise.all(concessionActionItemPromises);
        showAlert(
          `Created ${missingConcessionPreviews.length} action item${missingConcessionPreviews.length !== 1 ? 's' : ''} to verify concession info.`,
          'info'
        );
      }

      const messages = [
        `Updated ${newUnits.length} units`,
        createdCount > 0 ? `created ${createdCount} new leases` : null,
        updatedCount > 0 ? `updated ${updatedCount} existing leases` : null,
        cancelledCount > 0 ? `cancelled ${cancelledCount} old leases` : null,
      ].filter(Boolean);

      showAlert(messages.join(', ') + '.', 'success');

      // Show a separate warning if units were skipped
      if (skippedCount > 0) {
        showAlert(
          `${skippedCount} unit${skippedCount !== 1 ? 's were' : ' was'} skipped due to conflicts or no changes needed.`,
          'warning'
        );
      }

      // Refresh leases
      setUnitUpdates([]);
      setLeaseUpdates([]);
      await getLeases(
        { projectId },
        (leases: Lease[]) => {
          setExistingLeases(leases.filter((l) => l.status === 'valid'));
        }
      );
    } catch (err) {
      reportAppError(err as Error);
      showAlert('Failed to update units and leases', 'error');
    }

    loader.stopLoading();
  };

  const handleFixUnitTypesAndFloors = async () => {
    const loader = createLoading();

    loader.startLoading({
      title: 'Fixing Unit Types and Floors...',
    });

    try {
      const existingUnits = await getUnitsByProjectId(projectId);

      const updatedUnits = existingUnits.map((unit) => {
        const extracted = extractFloorAndType(unit.id);
        if (extracted) {
          return {
            ...unit,
            floor: parseInt(extracted.floor, 10),
            type: extracted.type,
          };
        }
        return unit;
      });

      await saveUnits(projectId, updatedUnits);
      showAlert('Successfully fixed unit types and floors', 'success');
    } catch (err) {
      reportAppError(err as Error);
      showAlert('Failed to fix unit types and floors', 'error');
    }

    loader.stopLoading();
  };

  const handleMarkAffordableUnits = async () => {
    const loader = createLoading();

    loader.startLoading({
      title: 'Marking Affordable Units...',
    });

    try {
      const existingUnits = await getUnitsByProjectId(projectId);

      const updatedUnits = existingUnits.map((unit) => {
        // Calculate annual rent per sqft ratio
        // Mark as affordable if ratio < 50
        if (unit.actualRent && unit.sqft && unit.sqft > 0) {
          const monthlyRent = unit.actualRent;
          const annualRent = monthlyRent * 12;
          const annualRentPerSqft = annualRent / unit.sqft;

          return {
            ...unit,
            affordable: annualRentPerSqft < 50,
          };
        }
        return unit;
      });

      await saveUnits(projectId, updatedUnits);
      showAlert('Successfully marked affordable units', 'success');
    } catch (err) {
      reportAppError(err as Error);
      showAlert('Failed to mark affordable units', 'error');
    }

    loader.stopLoading();
  };

  const rows: Partial<Unit>[] = unitUpdates;

  const gridColumns: GridColDef<(typeof rows)[number]>[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'leaseStartDate',
      headerName: 'Lease Start Date',
      type: 'date',
      width: 150,
      editable: true,
      renderCell: (params) => params.row.leaseStartDate ? dayjs(params.row.leaseStartDate).format('MM/DD/YYYY') : 'Vacant',
    },
    {
      field: 'leaseEndDate',
      type: 'date',
      headerName: 'Lease End Date',
      width: 150,
      editable: true,
      renderCell: (params) => params.row.leaseEndDate ? dayjs(params.row.leaseEndDate).format('MM/DD/YYYY') : '',
    },
    {
      field: 'actualRent',
      headerName: 'Rent',
      type: 'number',
      width: 150,
      editable: true,
      renderCell: (params) => params.row.actualRent ? params.row.actualRent.toLocaleString() : '--',
    },
    {
      field: 'residentDeposit',
      headerName: 'Resident Deposit',
      type: 'number',
      width: 150,
      editable: true,
      renderCell: (params) => params.row.residentDeposit ? params.row.residentDeposit.toLocaleString() : '--',
    },
  ];

  return (
    <Stack
      justifyContent="space-between"
      direction="row"
      alignItems="flex-start"
      className="w-full px-[100px] pb-[3em] pt-6"
      gap="30px"
    >
      <Stack gap="20px" className="rounded-2xl bg-white/5 p-24">
        <Typography variant="h4" color="textPrimary">
          Import Rent Roll
        </Typography>

        <Stack gap="20px">
          <FormControl>
            <Stack direction="row" gap="20px" alignItems="center">
              <Typography variant="h6" color="textPrimary">
                Import Format
              </Typography>
              <Select
                labelId="selected-input-format-label"
                id="selected-input-format"
                value={inputFormat}
                onChange={(e) => {
                  setInputFormat(e.target.value as InputFormats);
                  setUnitUpdates([]);
                  setLeaseUpdates([]);
                  setEntrataUnitFile(null);
                  setEntrataTenantFile(null);
                }}
              >
                <MenuItem value="appfolio">Appfolio</MenuItem>
                <MenuItem value="yardi-xls">Yardi XLS</MenuItem>
                <MenuItem value="yardi-csv">Yardi CSV</MenuItem>
                <MenuItem value="entrata-xls">Entrata XLS</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </Stack>
          </FormControl>
          {inputFormat === 'entrata-xls' ? (
            <Stack gap="10px">
              <Stack direction="row" gap="10px" alignItems="center">
                <FileUploadButton
                  accept=".xlsx,.xls"
                  label="1. Unit Directory"
                  onSelect={handleEntrataUnitFile}
                />
                {entrataUnitFile && (
                  <Chip label={entrataUnitFile} color="success" size="small" variant="outlined" />
                )}
              </Stack>
              <Stack direction="row" gap="10px" alignItems="center">
                <FileUploadButton
                  accept=".xlsx,.xls"
                  label="2. Tenant Listing"
                  onSelect={handleEntrataTenantFile}
                />
                {entrataTenantFile && (
                  <Chip label={entrataTenantFile} color="success" size="small" variant="outlined" />
                )}
              </Stack>
            </Stack>
          ) : (
            <FileUploadButton
              accept=".csv,.xlsx,.xls"
              label="Select File"
              onSelect={handleSelectFile}
            />
          )}
          <DataGrid
            rows={unitUpdates.map((unit) => ({ ...unit, id: unit.id || '' }))}
            columns={gridColumns}
            checkboxSelection
            autoHeight
            processRowUpdate={(newRow) => {
              setUnitUpdates((prev) =>
                prev.map((unit) => (unit.id === newRow.id ? newRow : unit))
              );
              return newRow;
            }}
            onProcessRowUpdateError={(error) => {
              showAlert(`Error while updating row: ${error.message}`, 'error');
            }}
            onRowSelectionModelChange={(selection) => {
              setSelectedUnits(selection.map((id: GridRowId) => id.toString()));
            }}
          />
        </Stack>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setUnitUpdates((prev) =>
              prev.filter((unit) => !selectedUnits.find((id) => id === unit.id))
            );
            setLeaseUpdates((prev) =>
              prev.filter((lease) => !selectedUnits.find((id) => id === lease.unitId))
            );
            setSelectedUnits([]);
          }}
        >
          Delete Selected Units
        </Button>

        <Stack direction="row" gap="10px" alignItems="center">
          <Button
            variant="text"
            size="small"
            onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
          >
            Admin Menu
          </Button>
          <Menu
            anchorEl={adminMenuAnchor}
            open={Boolean(adminMenuAnchor)}
            onClose={() => setAdminMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                handleFixUnitTypesAndFloors();
                setAdminMenuAnchor(null);
              }}
            >
              Fix Unit Types and Floors
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMarkAffordableUnits();
                setAdminMenuAnchor(null);
              }}
            >
              Mark Affordable Units (&lt;50 annual rent/sqft)
            </MenuItem>
          </Menu>
        </Stack>

        <ConfirmButtons
          confirm={{
            label: 'Preview & Save',
            onClick: () => {
              handleShowPreview();
            },
          }}
          cancel={{
            label: 'Cancel',
            onClick: () => {},
          }}
        />
      </Stack>

      {/* Lease Creation Preview Dialog */}
      {previewData && (
        <LeaseCreationPreviewDialog
          open={previewDialogOpen}
          onClose={handleClosePreview}
          onConfirm={handleConfirmImport}
          toCreate={previewData.toCreate}
          toSkip={previewData.toSkip}
          summary={previewData.summary}
        />
      )}
    </Stack>
  );
}
